import { ajaxBaseUrl, isNew } from '@/config'

const SHARED_ASSETS_VERSION_FILE = 'shared-assets-version.json'
const SHARED_ASSETS_VERSION_KEY = 'lexus-web-mini-shared-assets-version'
const WEB_SHARED_DOCUMENT_CACHE_NAME = 'lexus-web-mini-shared-document-v1'
const WEB_SHARED_STATIC_CACHE_NAME = 'lexus-web-mini-shared-static-v1'
const MINI_CACHE_NAMES = [
    'lexus-mini-static-v1',
    'lexus-mini-html-v1',
    'lexus-mini-data-v1',
    WEB_SHARED_DOCUMENT_CACHE_NAME,
    WEB_SHARED_STATIC_CACHE_NAME,
]
const MINI_WORKBOX_CACHE_PREFIX = 'mini-sw-'
const MANIFEST_REQUEST_TIMEOUT = 3000
const SERVICE_WORKER_ACTIVATION_TIMEOUT = 5000

let sharedAssetsRevision = ''

const getStoredRevision = () => {
    try {
        return localStorage.getItem(SHARED_ASSETS_VERSION_KEY) || ''
    } catch (error) {
        return ''
    }
}

const saveRevision = (revision) => {
    try {
        localStorage.setItem(SHARED_ASSETS_VERSION_KEY, revision)
    } catch (error) {
        console.warn('无法保存 mini 共享资源版本', error)
    }
}

const getMiniBaseUrl = () => {
    if (!ajaxBaseUrl) return null

    try {
        return new URL(`${ajaxBaseUrl.replace(/\/$/, '')}/`, window.location.origin)
    } catch (error) {
        return null
    }
}

const requestWithTimeout = (url, options) =>
    new Promise((resolve, reject) => {
        const timeoutId = window.setTimeout(() => {
            reject(new Error('共享资源版本请求超时'))
        }, MANIFEST_REQUEST_TIMEOUT)

        Promise.resolve()
            .then(() => window.fetch(url, options))
            .then(
                (response) => {
                    window.clearTimeout(timeoutId)
                    resolve(response)
                },
                (error) => {
                    window.clearTimeout(timeoutId)
                    reject(error)
                },
            )
    })

const loadSharedAssetsManifest = async () => {
    const miniBaseUrl = getMiniBaseUrl()
    if (!miniBaseUrl) return null

    // 每次启动都带随机参数，避免 CDN 或浏览器返回旧的版本清单。
    const manifestUrl = new URL(SHARED_ASSETS_VERSION_FILE, miniBaseUrl)
    // 避免依赖旧 WebView 对 URLSearchParams 的不完整实现。
    const manifestRequestUrl = `${manifestUrl.href}?t=${Date.now()}`
    const response = await requestWithTimeout(manifestRequestUrl, {
        cache: 'no-store',
        headers: {
            'Cache-Control': 'no-cache',
        },
    })

    if (!response.ok) {
        throw new Error(`共享资源版本请求失败：${response.status}`)
    }

    const manifest = await response.json()
    if (
        !manifest ||
        typeof manifest.revision !== 'string' ||
        !manifest.revision ||
        typeof manifest.serviceWorker !== 'string' ||
        !/^service-worker-\d+\.js$/.test(manifest.serviceWorker)
    ) {
        throw new Error('共享资源版本文件格式无效')
    }

    const serviceWorkerUrl = new URL(manifest.serviceWorker, miniBaseUrl)
    if (
        serviceWorkerUrl.origin !== miniBaseUrl.origin ||
        serviceWorkerUrl.pathname.indexOf(miniBaseUrl.pathname) !== 0
    ) {
        throw new Error('共享资源 Service Worker 地址无效')
    }

    return {
        revision: manifest.revision,
        miniBaseUrl,
        serviceWorkerUrl,
    }
}

const getMiniServiceWorkerRegistration = async (miniBaseUrl) => {
    const registrations = await navigator.serviceWorker.getRegistrations()
    return registrations.find((registration) => {
        try {
            return new URL(registration.scope).href === miniBaseUrl.href
        } catch (error) {
            return false
        }
    })
}

const isMiniCache = (cacheName) =>
    MINI_CACHE_NAMES.indexOf(cacheName) !== -1 ||
    cacheName.indexOf(MINI_WORKBOX_CACHE_PREFIX) === 0

const clearMiniCaches = async () => {
    if (!('caches' in window)) return

    const cacheNames = await caches.keys()
    await Promise.all(
        cacheNames
            .filter(isMiniCache)
            .map((cacheName) => caches.delete(cacheName)),
    )
}

const warmMiniBootstrapCache = async (miniBaseUrl) => {
    if (!('caches' in window)) return

    // public/index.html 在应用入口之前加载该脚本。首次安装以及 revision
    // 变更清缓存后主动补回，保证当前页面之后已具备离线回退。
    const panoPlayerUrl = new URL('pano2vr_player.js', miniBaseUrl)
    const response = await requestWithTimeout(panoPlayerUrl.href, {
        cache: 'no-store',
    })
    if (!response.ok) {
        throw new Error(`pano2vr_player.js 请求失败：${response.status}`)
    }

    const cache = await caches.open(WEB_SHARED_DOCUMENT_CACHE_NAME)
    await cache.put(panoPlayerUrl.href, response)
}

const hasActiveWorker = (registration, serviceWorkerUrl) =>
    Boolean(
        registration &&
        registration.active &&
        registration.active.scriptURL === serviceWorkerUrl.href,
    )

const waitForServiceWorkerActivation = (registration, serviceWorkerUrl) =>
    new Promise((resolve, reject) => {
        const startedAt = Date.now()
        const timer = window.setInterval(() => {
            if (hasActiveWorker(registration, serviceWorkerUrl)) {
                window.clearInterval(timer)
                resolve()
                return
            }

            if (Date.now() - startedAt >= SERVICE_WORKER_ACTIVATION_TIMEOUT) {
                window.clearInterval(timer)
                reject(new Error('mini Service Worker 激活超时'))
            }
        }, 50)
    })

const updateMiniServiceWorker = async (manifest) => {
    if (!('serviceWorker' in navigator)) return false

    let registration = await getMiniServiceWorkerRegistration(manifest.miniBaseUrl)
    if (hasActiveWorker(registration, manifest.serviceWorkerUrl)) return false

    registration = await navigator.serviceWorker.register(
        manifest.serviceWorkerUrl.href,
        {
            scope: manifest.miniBaseUrl.pathname,
            updateViaCache: 'none',
        },
    )
    await waitForServiceWorkerActivation(registration, manifest.serviceWorkerUrl)
    return true
}

export const syncSharedMiniAssets = async () => {
    sharedAssetsRevision = getStoredRevision()

    if (process.env.NODE_ENV !== 'production' || isNew) {
        return sharedAssetsRevision
    }

    try {
        const manifest = await loadSharedAssetsManifest()
        if (!manifest) return sharedAssetsRevision

        const revisionChanged = sharedAssetsRevision !== manifest.revision
        const serviceWorkerChanged = await updateMiniServiceWorker(manifest)

        // 新 Worker 已经激活后再清理旧缓存，避免 iframe 继续使用旧版本资源。
        if (revisionChanged || serviceWorkerChanged) {
            await clearMiniCaches()
            await warmMiniBootstrapCache(manifest.miniBaseUrl)
        }

        sharedAssetsRevision = manifest.revision
        saveRevision(manifest.revision)
    } catch (error) {
        // 共享资源更新失败不阻塞官网；下次进入页面时会继续重试。
        console.warn('mini 共享资源版本检查失败，继续使用当前资源', error)
    }

    return sharedAssetsRevision
}

export const getSharedAssetsRevision = () => sharedAssetsRevision
